/**
 * Utility functions for interpreting workout intensity metrics
 * Based on backend team's interpretation guidelines
 */

import type { IntensityFocus } from '../../../store/api/models/workoutModels';

// ============================================================================
// Overall Intensity Score Interpretation (0-1 scale)
// ============================================================================

export interface IntensityScoreInterpretation {
  level: 'excellent' | 'good' | 'fair' | 'needs-improvement';
  label: string;
  message: string;
  color: string;
  description: string;
}

export const getIntensityScoreInterpretation = (
  score: number | null | undefined
): IntensityScoreInterpretation => {
  if (score === null || score === undefined) {
    return {
      level: 'needs-improvement',
      label: 'No Data',
      message: 'No intensity target specified for this segment',
      color: '#9e9e9e',
      description: 'Unable to evaluate intensity control',
    };
  }

  if (score >= 0.85) {
    return {
      level: 'excellent',
      label: 'Excellent',
      message: 'You maintained target intensity throughout most of the workout. Great discipline!',
      color: '#66bb6a',
      description: 'Optimal training stimulus, proper recovery balance, sustainable progress.',
    };
  }

  if (score >= 0.70) {
    return {
      level: 'good',
      label: 'Good',
      message: 'You stayed close to target intensity with minor deviations. Keep it up!',
      color: '#42a5f5',
      description: 'Effective training stimulus with room for refinement.',
    };
  }

  if (score >= 0.50) {
    return {
      level: 'fair',
      label: 'Fair',
      message: 'You had notable deviations from target intensity. Review your pacing strategy.',
      color: '#ffa726',
      description: 'Training stimulus may be inconsistent. Risk of suboptimal adaptation.',
    };
  }

  return {
    level: 'needs-improvement',
    label: 'Needs Improvement',
    message: 'Significant deviations from target detected. Consider adjusting your approach.',
    color: '#ef5350',
    description: 'High risk of overtraining or insufficient stimulus. Training adaptations may be compromised.',
  };
};

// ============================================================================
// Directional Deviation Interpretation
// ============================================================================

export interface DeviationInterpretation {
  severity: 'minimal' | 'moderate' | 'significant' | 'severe';
  message: string;
  color: string;
  recommendation: string;
}

// Heart Rate Deviation Interpretation
export const getHRDeviationAboveInterpretation = (
  avgDeviationBpm: number | null | undefined
): DeviationInterpretation | null => {
  if (avgDeviationBpm === null || avgDeviationBpm === undefined) return null;

  if (avgDeviationBpm <= 5) {
    return {
      severity: 'minimal',
      message: 'Slightly above target - well controlled',
      color: '#66bb6a',
      recommendation: 'Minimal concern. Continue with current approach.',
    };
  }

  if (avgDeviationBpm <= 10) {
    return {
      severity: 'moderate',
      message: 'Moderately above target - watch your pacing',
      color: '#ffa726',
      recommendation: 'Consider starting more conservatively or checking your pacing strategy.',
    };
  }

  if (avgDeviationBpm <= 20) {
    return {
      severity: 'significant',
      message: 'Significantly above target - risk of overexertion',
      color: '#ff7043',
      recommendation: 'High risk of poor recovery. Slow down to avoid overtraining.',
    };
  }

  return {
    severity: 'severe',
    message: 'Well above target - high risk of burnout',
    color: '#ef5350',
    recommendation: 'Severe overshoot. Significantly reduce intensity to prevent injury and burnout.',
  };
};

export const getHRDeviationBelowInterpretation = (
  avgDeviationBpm: number | null | undefined
): DeviationInterpretation | null => {
  if (avgDeviationBpm === null || avgDeviationBpm === undefined) return null;

  if (avgDeviationBpm <= 5) {
    return {
      severity: 'minimal',
      message: 'Slightly below target - acceptable effort',
      color: '#66bb6a',
      recommendation: 'Acceptable performance. Training effect will still be achieved.',
    };
  }

  if (avgDeviationBpm <= 10) {
    return {
      severity: 'moderate',
      message: 'Moderately below target - push a bit harder',
      color: '#42a5f5',
      recommendation: 'Increase effort slightly for better training adaptations.',
    };
  }

  if (avgDeviationBpm <= 20) {
    return {
      severity: 'significant',
      message: 'Significantly below target - insufficient stimulus',
      color: '#ffa726',
      recommendation: 'Training stimulus is compromised. Push harder to get desired benefits.',
    };
  }

  return {
    severity: 'severe',
    message: 'Well below target - minimal training benefit',
    color: '#ef5350',
    recommendation: 'Intensity too low. Increase significantly to achieve training goals.',
  };
};

// Pace Deviation Interpretation (seconds per km)
export const getPaceDeviationAboveInterpretation = (
  avgDeviationSecPerKm: number | null | undefined
): DeviationInterpretation | null => {
  if (avgDeviationSecPerKm === null || avgDeviationSecPerKm === undefined) return null;

  if (avgDeviationSecPerKm <= 10) {
    return {
      severity: 'minimal',
      message: 'Slightly faster than target',
      color: '#66bb6a',
      recommendation: 'Minimal concern. Pace is well controlled.',
    };
  }

  if (avgDeviationSecPerKm <= 20) {
    return {
      severity: 'moderate',
      message: 'Noticeably faster than target - may not be sustainable',
      color: '#ffa726',
      recommendation: 'Consider slowing down to ensure you can maintain effort through the workout.',
    };
  }

  if (avgDeviationSecPerKm <= 40) {
    return {
      severity: 'significant',
      message: 'Much faster than target - risk of early fatigue',
      color: '#ff7043',
      recommendation: 'Significantly too fast. High risk of burnout before workout completion.',
    };
  }

  return {
    severity: 'severe',
    message: 'Extremely fast pace - unsustainable',
    color: '#ef5350',
    recommendation: 'Pace is far too fast for this workout type. Risk of failure to complete.',
  };
};

export const getPaceDeviationBelowInterpretation = (
  avgDeviationSecPerKm: number | null | undefined
): DeviationInterpretation | null => {
  if (avgDeviationSecPerKm === null || avgDeviationSecPerKm === undefined) return null;

  if (avgDeviationSecPerKm <= 10) {
    return {
      severity: 'minimal',
      message: 'Slightly slower than target',
      color: '#66bb6a',
      recommendation: 'Acceptable performance. Minor adjustment needed.',
    };
  }

  if (avgDeviationSecPerKm <= 20) {
    return {
      severity: 'moderate',
      message: 'Noticeably slower than target',
      color: '#42a5f5',
      recommendation: 'Push harder for better training gains.',
    };
  }

  if (avgDeviationSecPerKm <= 40) {
    return {
      severity: 'significant',
      message: 'Much slower than target - insufficient stimulus',
      color: '#ffa726',
      recommendation: 'Pace too slow. Increase significantly to achieve desired training effect.',
    };
  }

  return {
    severity: 'severe',
    message: 'Too slow - minimal training benefit',
    color: '#ef5350',
    recommendation: 'Extremely slow pace. Increase effort substantially.',
  };
};

// Power Deviation Interpretation (watts)
export const getPowerDeviationAboveInterpretation = (
  avgDeviationWatts: number | null | undefined
): DeviationInterpretation | null => {
  if (avgDeviationWatts === null || avgDeviationWatts === undefined) return null;

  if (avgDeviationWatts <= 10) {
    return {
      severity: 'minimal',
      message: 'Slightly above target power',
      color: '#66bb6a',
      recommendation: 'Well controlled power output.',
    };
  }

  if (avgDeviationWatts <= 25) {
    return {
      severity: 'moderate',
      message: 'Moderately above target power',
      color: '#ffa726',
      recommendation: 'Consider moderating power output for better sustainability.',
    };
  }

  if (avgDeviationWatts <= 50) {
    return {
      severity: 'significant',
      message: 'Significantly above target power',
      color: '#ff7043',
      recommendation: 'Power output too high. Risk of premature fatigue.',
    };
  }

  return {
    severity: 'severe',
    message: 'Well above target power',
    color: '#ef5350',
    recommendation: 'Excessive power output. Reduce significantly to avoid burnout.',
  };
};

export const getPowerDeviationBelowInterpretation = (
  avgDeviationWatts: number | null | undefined
): DeviationInterpretation | null => {
  if (avgDeviationWatts === null || avgDeviationWatts === undefined) return null;

  if (avgDeviationWatts <= 10) {
    return {
      severity: 'minimal',
      message: 'Slightly below target power',
      color: '#66bb6a',
      recommendation: 'Acceptable power output.',
    };
  }

  if (avgDeviationWatts <= 25) {
    return {
      severity: 'moderate',
      message: 'Moderately below target power',
      color: '#42a5f5',
      recommendation: 'Increase power output for better training effect.',
    };
  }

  if (avgDeviationWatts <= 50) {
    return {
      severity: 'significant',
      message: 'Significantly below target power',
      color: '#ffa726',
      recommendation: 'Power too low. Increase substantially for desired stimulus.',
    };
  }

  return {
    severity: 'severe',
    message: 'Well below target power',
    color: '#ef5350',
    recommendation: 'Power output far too low. Training benefit is minimal.',
  };
};

// Generic deviation interpretation dispatcher
export const getDeviationInterpretation = (
  intensityFocus: IntensityFocus | null | undefined,
  avgDeviationAbove: number | null | undefined,
  avgDeviationBelow: number | null | undefined
): {
  above: DeviationInterpretation | null;
  below: DeviationInterpretation | null;
} => {
  if (!intensityFocus || intensityFocus === 'none') {
    return { above: null, below: null };
  }

  let above: DeviationInterpretation | null = null;
  let below: DeviationInterpretation | null = null;

  switch (intensityFocus) {
    case 'hr':
      above = getHRDeviationAboveInterpretation(avgDeviationAbove);
      below = getHRDeviationBelowInterpretation(avgDeviationBelow);
      break;
    case 'pace':
      above = getPaceDeviationAboveInterpretation(avgDeviationAbove);
      below = getPaceDeviationBelowInterpretation(avgDeviationBelow);
      break;
    case 'power':
      above = getPowerDeviationAboveInterpretation(avgDeviationAbove);
      below = getPowerDeviationBelowInterpretation(avgDeviationBelow);
      break;
  }

  return { above, below };
};

// ============================================================================
// Workout-Level Time Distribution Interpretation
// ============================================================================

export interface TimeDistributionInterpretation {
  severity: 'excellent' | 'good' | 'fair' | 'poor';
  message: string;
  color: string;
}

export const getWorkoutAbovePctInterpretation = (
  abovePct: number | null | undefined
): TimeDistributionInterpretation | null => {
  if (abovePct === null || abovePct === undefined) return null;

  if (abovePct <= 10) {
    return {
      severity: 'excellent',
      message: 'Mostly on target with minimal overshoots',
      color: '#66bb6a',
    };
  }

  if (abovePct <= 25) {
    return {
      severity: 'good',
      message: 'Moderate time above target - consider better pacing',
      color: '#ffa726',
    };
  }

  if (abovePct <= 50) {
    return {
      severity: 'fair',
      message: 'Significant time above target - high risk of overtraining',
      color: '#ff7043',
    };
  }

  return {
    severity: 'poor',
    message: 'Majority of workout too intense - recovery may be compromised',
    color: '#ef5350',
  };
};

export const getWorkoutBelowPctInterpretation = (
  belowPct: number | null | undefined
): TimeDistributionInterpretation | null => {
  if (belowPct === null || belowPct === undefined) return null;

  if (belowPct <= 10) {
    return {
      severity: 'excellent',
      message: 'Mostly on target with minimal undershoots',
      color: '#66bb6a',
    };
  }

  if (belowPct <= 25) {
    return {
      severity: 'good',
      message: 'Moderate time below target - push harder for better results',
      color: '#42a5f5',
    };
  }

  if (belowPct <= 50) {
    return {
      severity: 'fair',
      message: 'Significant time below target - training stimulus reduced',
      color: '#ffa726',
    };
  }

  return {
    severity: 'poor',
    message: 'Majority of workout too easy - increase intensity for progress',
    color: '#ef5350',
  };
};

// ============================================================================
// Formatting Utilities
// ============================================================================

export const formatDeviation = (
  deviation: number | null | undefined,
  intensityFocus: IntensityFocus | null | undefined
): string => {
  if (deviation === null || deviation === undefined || !intensityFocus) {
    return 'N/A';
  }

  switch (intensityFocus) {
    case 'hr':
      return `${deviation.toFixed(1)} bpm`;
    case 'pace':
      const mins = Math.floor(deviation / 60);
      const secs = Math.round(deviation % 60);
      return `${mins}:${secs.toString().padStart(2, '0')}/km`;
    case 'power':
      return `${deviation.toFixed(0)}W`;
    default:
      return 'N/A';
  }
};

export const getDeviationUnit = (intensityFocus: IntensityFocus | null | undefined): string => {
  switch (intensityFocus) {
    case 'hr':
      return 'bpm';
    case 'pace':
      return 'sec/km';
    case 'power':
      return 'W';
    default:
      return '';
  }
};
